import {
  composeEvent,
  composeEventCompetitors
} from "@fresh8/ad-data-composition/src/betData";
import {
  injectBrand,
  injectAdTag
} from "@fresh8/ad-data-composition/src/brand";
import { injectPreview } from "@fresh8/ad-data-composition/src/preview";
import { SportCompetitor } from "@fresh8/types/src/betData";
import { Sport, SportEnum } from "@fresh8/types/src/language";
import { computed, ComputedRef, inject, provide } from "vue";
import { areColorsAvailable } from "@fresh8/ad-data-composition/src/lib/setBannerColors";
import { checkUseLeagueLogo } from "@fresh8/ad-data-composition/src/betData";

export const enabledSports: Sport[] = [
  SportEnum.FOOTBALL,
  SportEnum.AMERICAN_FOOTBALL,
  SportEnum.BASEBALL,
  SportEnum.BASKETBALL,
  SportEnum.ICE_HOCKEY
];

export function useTeamJerseys(
  eventId: string,
  brand = injectBrand()
): ComputedRef<boolean> {
  const useLeagueLogo = checkUseLeagueLogo();
  if (useLeagueLogo.value) return computed(() => false);
  const previewContext = injectPreview();
  const event = composeEvent(eventId);
  const adTagInjected = injectAdTag();
  const operator = computed(() => adTagInjected?.operator.value?._id);
  const sport = computed(() => event.value?.sport as Sport);
  const competition = computed(
    () => event.value && (event.value?.normalisedCompetition as string)
  );
  const competitors = composeEventCompetitors(eventId) as ComputedRef<
    SportCompetitor[]
  >;

  // Filters Set in Console
  const filters = computed(
    () => brand?.adTemplate?.value?.team?.badge?.filters
  );

  const operatorFilter = computed(() =>
    filters.value?.find(filter => filter.operator == operator.value)
  );

  /**
   * Test various filters, all need to be true for enabling
   *
   * @const teamJerseysEnabled : toggle in console to turn on/off team jerseys
   *
   * @const isOperatorEnabled: check if specific operator has been enabled in console (on/off toggle in console).
   *
   * @const isSportEnabled: check which sports have been enabled in console, if empty enable all.
   *
   * @const isEnabledSport: check if the sport is an sport that can have teamBranding - see enabledSport const line 11 ^^^
   *
   * @const isCompetitionEnabled: check which sports have been enabled in console, if empty enable all.
   *
   */

  const isOperatorEnabled = computed(() => !!operatorFilter.value);

  const isSportEnabled = computed(() =>
    operatorFilter.value?.sports?.length == 0
      ? true
      : event.value && operatorFilter.value?.sports?.includes(sport.value)
  );

  const isEnabledSport = computed(
    () => enabledSports.findIndex(s => s === event.value?.sport) !== -1
  );

  const isCompetitionEnabled = computed(() =>
    operatorFilter.value?.competitions?.length === 0
      ? true
      : event.value &&
        operatorFilter.value?.competitions?.includes(
          competition.value as string
        )
  );

  const teamJerseysEnabled = computed(
    () => !!brand?.adTemplate?.value?.team?.useTeamJerseys
  );

  return computed(() => {
    if (
      !event.value?.competitors.away.name.full &&
      !event.value?.competitors.home.name.full
    ) {
      return false;
    }

    // Hide jerseys if international badges are available
    if (
      event.value?.competitors?.away?.colors?.country_code_a2 &&
      event.value?.competitors?.home?.colors?.country_code_a2
    ) {
      return false;
    }

    // If being display in the preivew then always display the jerseys if branding is enabled
    if (
      isEnabledSport.value &&
      teamJerseysEnabled.value &&
      previewContext?.preview.value.isPreview &&
      areColorsAvailable(competitors.value)
    ) {
      return true;
    }

    // Filter by sport and operator
    if (
      teamJerseysEnabled.value &&
      !previewContext?.preview.value.isPreview &&
      isOperatorEnabled.value &&
      isSportEnabled.value &&
      isEnabledSport.value &&
      isCompetitionEnabled.value &&
      areColorsAvailable(competitors.value)
    ) {
      return true;
    }

    return false;
  });
}

type UseTeamJerseysContext = ReturnType<typeof useTeamJerseys>;
const UseTeamJerseysSymbol = Symbol("UseTeamJerseys");

// Creating the useTeamJerseys context to provide down the dependancy tree
// reduces the amount of repeated computed properties are made.
export const provideUseTeamJerseys = (
  eventId: string
): UseTeamJerseysContext => {
  const useTeamJerseysContext = useTeamJerseys(eventId);
  provide<UseTeamJerseysContext>(UseTeamJerseysSymbol, useTeamJerseysContext);
  return useTeamJerseysContext;
};

// If the context is undefined when calling the inject, then create the
// context and return the data anyway.
export const injectUseTeamJerseys = (): UseTeamJerseysContext => {
  return inject<UseTeamJerseysContext>(
    UseTeamJerseysSymbol,
    computed(() => false)
  );
};
